/**
 * DesktopPickerOverlayView - Dropdown card content for desktop
 *
 * Renders action items as a styled dropdown card grouped into two sections:
 * 1. Standard actions: "Add a report", "Add to list"
 * 2. Management actions (manual players only, separated by a divider):
 *    "Edit player", "Transfer reports & notes" (amber, disabled when 0 data),
 *    and "Delete player" (red, always last).
 * Visibility and positioning are handled by the parent Popover component.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import {
  ArrowRightLeft,
  FileText,
  ListPlus,
  Pencil,
  Trash2,
} from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Divider } from "@/components/atoms/Divider";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/** Pressable row class for picker items (theme owns hover/active surface). */
const DESKTOP_ROW_CLASS =
  "flex-row items-center gap-3 rounded-md px-3 py-2 active:bg-surface-subtle";

/** Accessibility state for disabled transfer item */
const TRANSFER_DISABLED_STATE = { disabled: true } as const;

/** Accessibility state for enabled transfer item */
const TRANSFER_ENABLED_STATE = { disabled: false } as const;

/** Class for disabled transfer item */
const TRANSFER_DISABLED_CLASS = "opacity-40";

/**
 * Transfer item icon/text class — amber accent when enabled, muted when disabled
 * @param isDisabled - Whether the transfer action is disabled
 */
const getTransferAccentClass = (isDisabled?: boolean): string =>
  isDisabled ? "text-content-muted" : "text-status-warning";

/**
 * Whether any management actions are available (controls divider visibility)
 * @param onEditPlayer - Edit player callback
 * @param onTransferReports - Transfer reports callback
 * @param onDeletePlayer - Delete player callback
 */
const hasManagementActions = (
  onEditPlayer?: () => void,
  onTransferReports?: () => void,
  onDeletePlayer?: () => void
): boolean => Boolean(onEditPlayer || onTransferReports || onDeletePlayer);

/**
 * Get transfer item accessibility state based on disabled flag
 * @param isDisabled - Whether the transfer action is disabled
 */
const getTransferAccessibilityState = (isDisabled?: boolean) =>
  isDisabled ? TRANSFER_DISABLED_STATE : TRANSFER_ENABLED_STATE;

/**
 * Props for DesktopPickerOverlayView
 */
export interface DesktopPickerOverlayViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Callback for "Add a report" option (hidden if undefined) */
  readonly onAddReport?: () => void;
  /** Callback for "Add to list" option (hidden if undefined) */
  readonly onAddToList?: () => void;
  /** Callback for "Edit player" option — manual players only (hidden if undefined) */
  readonly onEditPlayer?: () => void;
  /** Callback for "Delete player" option — manual players only (hidden if undefined) */
  readonly onDeletePlayer?: () => void;
  /** Callback for "Transfer reports & notes" — admin users on manual players only (hidden if undefined) */
  readonly onTransferReports?: () => void;
  /** Whether the transfer reports action is disabled (0 reports and 0 notes) */
  readonly isTransferDisabled?: boolean;
}

/**
 * DesktopPickerOverlayView renders the dropdown card content with action rows
 * for reports, lists, and manual player management. Positioning and visibility
 * are controlled by the parent Popover.
 * @param props - View props for the picker overlay
 * @param props.onAddReport - Callback for "Add a report"
 * @param props.onAddToList - Callback for "Add to list"
 * @param props.onEditPlayer - Callback for "Edit player" (manual players only)
 * @param props.onDeletePlayer - Callback for "Delete player" (manual players only)
 * @param props.onTransferReports - Callback for "Transfer reports & notes" (admin on manual players)
 * @param props.isTransferDisabled - Whether the transfer action is disabled (0 reports and 0 notes)
 * @returns Card with action rows
 */
const DesktopPickerOverlayView = ({
  onAddReport,
  onAddToList,
  onEditPlayer,
  onDeletePlayer,
  onTransferReports,
  isTransferDisabled,
}: DesktopPickerOverlayViewProps) => (
  <Box className="border-border-outline-subtle min-w-44 rounded-md border bg-surface-raised shadow-soft-2">
    <Stack className="gap-micro py-1">
      {onAddReport ? (
        <Pressable
          onPress={onAddReport}
          className={DESKTOP_ROW_CLASS}
          accessibilityRole="button"
          accessibilityLabel="Add a report"
        >
          <Icon as={FileText} size="sm" className="text-content-secondary" />
          <Text variant="unstyled" className="text-content-primary">
            Add a report
          </Text>
        </Pressable>
      ) : null}
      {onAddToList ? (
        <Pressable
          onPress={onAddToList}
          className={DESKTOP_ROW_CLASS}
          accessibilityRole="button"
          accessibilityLabel="Add to list"
        >
          <Icon as={ListPlus} size="sm" className="text-content-secondary" />
          <Text variant="unstyled" className="text-content-primary">
            Add to list
          </Text>
        </Pressable>
      ) : null}
      {(onAddReport || onAddToList) &&
      hasManagementActions(onEditPlayer, onTransferReports, onDeletePlayer) ? (
        <Stack className="py-1">
          <Divider className="bg-border-outline-subtle" />
        </Stack>
      ) : null}
      {onEditPlayer ? (
        <Pressable
          onPress={onEditPlayer}
          className={DESKTOP_ROW_CLASS}
          accessibilityRole="button"
          accessibilityLabel="Edit player"
        >
          <Icon as={Pencil} size="sm" className="text-content-secondary" />
          <Text variant="unstyled" className="text-content-primary">
            Edit player
          </Text>
        </Pressable>
      ) : null}
      {onTransferReports ? (
        <Pressable
          onPress={isTransferDisabled ? undefined : onTransferReports}
          className={`${DESKTOP_ROW_CLASS}${isTransferDisabled ? ` ${TRANSFER_DISABLED_CLASS}` : ""}`}
          accessibilityRole="button"
          accessibilityLabel="Transfer reports and notes"
          accessibilityState={getTransferAccessibilityState(isTransferDisabled)}
          disabled={isTransferDisabled}
        >
          <Icon
            as={ArrowRightLeft}
            size="sm"
            className={getTransferAccentClass(isTransferDisabled)}
          />
          <Text
            variant="unstyled"
            className={getTransferAccentClass(isTransferDisabled)}
          >
            Transfer reports & notes
          </Text>
        </Pressable>
      ) : null}
      {onDeletePlayer ? (
        <Pressable
          onPress={onDeletePlayer}
          className={DESKTOP_ROW_CLASS}
          accessibilityRole="button"
          accessibilityLabel="Delete player"
        >
          <Icon as={Trash2} size="sm" className="text-status-error" />
          <Text variant="unstyled" className="text-status-error">
            Delete player
          </Text>
        </Pressable>
      ) : null}
    </Stack>
  </Box>
);

DesktopPickerOverlayView.displayName = "DesktopPickerOverlayView";

export default memo(DesktopPickerOverlayView);
